/**
 * UserLogClient — the frontend counterpart to backend/src/service/user_log_service.ts.
 *
 * Responsibilities:
 *   - Accept events via logEvent() from anywhere in the app (zero UX impact).
 *   - Buffer events in memory and flush in batches to reduce network chatter.
 *   - Flush reliably on page unload using navigator.sendBeacon() (falls back
 *     to fetch keepalive).
 *   - NEVER throw, NEVER block the UI. Any emission error is swallowed.
 *   - Backend endpoint /logs/batch is public (see authenticator.ts), so we do
 *     NOT attach an Authorization header. Identity (user_id / user_role /
 *     user_email) is supplied by the React layer via setIdentity() and sent
 *     in the payload. Backend trusts these for telemetry purposes; IP is
 *     always server-authoritative.
 *
 * Design notes:
 *   - Singleton: one queue per page. Keeps wiring simple and prevents
 *     interleaved flushes from competing buffers.
 *   - Triggers for flushing:
 *       * every FLUSH_INTERVAL_MS (timer)
 *       * when queue length reaches QUEUE_THRESHOLD
 *       * when document transitions to hidden (visibilitychange)
 *       * on pagehide (most reliable unload hook; replaces beforeunload)
 *   - Max queue size MAX_QUEUE_SIZE caps memory if backend is unreachable;
 *     oldest events are dropped rather than consuming unbounded memory.
 */

import { LogIdentity, LogSource, UserLogEventInput, UserLogRecordOut } from "./types";
import { getDeviceContext } from "./deviceInfo";

const FLUSH_INTERVAL_MS = 10_000;
const QUEUE_THRESHOLD = 20;
const MAX_QUEUE_SIZE = 500;
/** Max events per single POST — mirrors backend UserLogController MAX_BATCH. */
const MAX_BATCH = 100;

interface ClientOptions {
	endpoint: string;
	enabled: boolean;
}

class UserLogClient {
	private queue: UserLogRecordOut[] = [];
	private identity: LogIdentity = { userId: null, userEmail: null, userRoles: null };
	private page: string | null = null;
	private flushTimer: number | null = null;
	private started = false;
	private options: ClientOptions = { endpoint: "", enabled: false };

	/**
	 * Wire the client into the page. Safe to call multiple times — subsequent
	 * calls are no-ops. Should be called once at app bootstrap.
	 */
	start(options: ClientOptions) {
		if (this.started) return;
		this.options = options;
		this.started = true;

		if (!options.enabled || !options.endpoint) {
			return;
		}

		this.flushTimer = window.setInterval(() => {
			if (this.queue.length > 0) this.flush({ useBeacon: false });
		}, FLUSH_INTERVAL_MS);

		// The most reliable unload hook. pagehide fires on BFCache evictions
		// and tab/window close; beforeunload is unreliable on mobile.
		window.addEventListener("pagehide", this.handlePageHide);
		document.addEventListener("visibilitychange", this.handleVisibilityChange);
	}

	/** Tear down timers/listeners. Intended for tests/HMR. */
	stop() {
		if (!this.started) return;
		if (this.flushTimer !== null) {
			window.clearInterval(this.flushTimer);
			this.flushTimer = null;
		}
		window.removeEventListener("pagehide", this.handlePageHide);
		document.removeEventListener("visibilitychange", this.handleVisibilityChange);
		this.started = false;
	}

	setIdentity(identity: Partial<LogIdentity>) {
		this.identity = {
			userId: identity.userId ?? null,
			userEmail: identity.userEmail ?? null,
			userRoles: identity.userRoles ?? null,
		};
	}

	clearIdentity() {
		this.identity = { userId: null, userEmail: null, userRoles: null };
	}

	/** Called by RouteTracker whenever the route changes. */
	setCurrentPage(page: string | null) {
		this.page = page;
	}

	getCurrentPage(): string | null {
		return this.page;
	}

	/**
	 * Queue an event for emission. Never throws. Returns immediately.
	 */
	logEvent(event: UserLogEventInput) {
		if (!this.options.enabled || !this.options.endpoint) return;

		try {
			const record = this.buildRecord(event);
			this.queue.push(record);

			// Cap queue size — drop oldest when overflowing.
			if (this.queue.length > MAX_QUEUE_SIZE) {
				this.queue.splice(0, this.queue.length - MAX_QUEUE_SIZE);
			}

			if (this.queue.length >= QUEUE_THRESHOLD) {
				this.flush({ useBeacon: false });
			}
		} catch (err) {
			// Absolutely must not crash the UI. Log once and move on.
			// eslint-disable-next-line no-console
			console.warn("[UserLog] logEvent failed:", err);
		}
	}

	/**
	 * Flush the current queue. Safe to call when empty.
	 * useBeacon=true switches to navigator.sendBeacon for unload-reliable delivery.
	 */
	flush(opts: { useBeacon: boolean }) {
		if (!this.options.enabled || !this.options.endpoint) return;
		if (this.queue.length === 0) return;

		const batchesToSend: UserLogRecordOut[][] = [];
		while (this.queue.length > 0) {
			batchesToSend.push(this.queue.splice(0, MAX_BATCH));
		}

		for (const batch of batchesToSend) {
			try {
				const payload = JSON.stringify({ events: batch });
				if (opts.useBeacon) {
					this.sendBeacon(payload);
				} else {
					this.sendFetch(payload);
				}
			} catch (err) {
				// eslint-disable-next-line no-console
				console.warn("[UserLog] flush failed:", err);
			}
		}
	}

	// --- private ---------------------------------------------------------

	private buildRecord(event: UserLogEventInput): UserLogRecordOut {
		const device = getDeviceContext();
		const userRoles = this.identity.userRoles?.length ? this.identity.userRoles.join(",") : null;

		const properties = event.properties ? { ...event.properties } : null;

		let propertiesStr: string | null = null;
		if (properties) {
			try {
				propertiesStr = JSON.stringify(properties);
			} catch {
				// Circular references or serialization errors — drop properties
				// rather than the whole event.
				propertiesStr = null;
			}
		}

		return {
			source: LogSource.FRONTEND,
			action: event.action,
			user_id: this.identity.userId,
			user_email: this.identity.userEmail,
			user_role: userRoles,
			device_id: device.deviceId,
			operating_system: device.operatingSystem,
			browsing_environment: device.userAgent,
			campaign_id: event.campaign_id ?? null,
			block_id: event.block_id ?? null,
			item_id: event.item_id ?? null,
			client_timestamp: new Date().toISOString(),
			duration: event.duration ?? null,
			loading_time: event.loading_time ?? null,
			result_status: event.result_status ?? null,
			error_code: event.error_code ?? null,
			page: this.page,
			properties: propertiesStr,
		};
	}

	private sendBeacon(payload: string) {
		try {
			if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
				// Beacons expect a Blob with the right content type for the
				// server to parse JSON. This is the documented pattern.
				const blob = new Blob([payload], { type: "application/json" });
				const ok = navigator.sendBeacon(this.options.endpoint, blob);
				if (ok) return;
			}
		} catch {
			// fall through to keepalive fetch
		}
		// Fallback: fetch keepalive. Less reliable on unload than beacon but
		// still non-blocking and survives most navigations.
		this.sendFetch(payload, { keepalive: true });
	}

	private sendFetch(payload: string, extras: RequestInit = {}) {
		try {
			// Intentionally NOT awaited — fire-and-forget. Any rejection
			// (network, 4xx, 5xx) is caught and swallowed; telemetry must
			// never surface errors to the user.
			void fetch(this.options.endpoint, {
				method: "POST",
				credentials: "include",
				headers: { "Content-Type": "application/json" },
				body: payload,
				...extras,
			}).catch(() => {
				/* swallow */
			});
		} catch {
			/* swallow */
		}
	}

	private handleVisibilityChange = () => {
		if (document.visibilityState === "hidden" && this.queue.length > 0) {
			// Hidden ≠ unload, but on mobile this is often the last chance we
			// get before the OS freezes the tab. Use beacon for reliability.
			this.flush({ useBeacon: true });
		}
	};

	private handlePageHide = () => {
		if (this.queue.length > 0) {
			this.flush({ useBeacon: true });
		}
	};
}

export const userLogClient = new UserLogClient();
